import { useQuery } from "@tanstack/react-query";
import type { Transaction } from "@/db/db.schema";
import { expensesAtom, type ExpenseWithRequiredCategory } from "@/store/global";
import { userAtom } from "@/store/user";
import { useNetwork } from "@mantine/hooks";
import { useAtom } from "jotai";
import type { Omit } from "utility-types";
import { useWindowFocus } from "./useWindowFocus";
import { getCookies } from "@/helpers/cookie";
import { useEffect } from "react";

import axios from "axios";

const fetchTransactions = async () => {
  const res = await axios.get("/api/transactions");

  if (res.status !== 200) {
    throw new Error("Failed to fetch transactions");
  }

  return res.data as Promise<{
    transactions: Omit<Transaction, "id">[];
    deletedIds: string[];
  }>;
};

export const useGetTransactions = () => {
  const [{ expenses }, setExpenses] = useAtom(expensesAtom);
  const [user] = useAtom(userAtom);
  const isWindowFocused = useWindowFocus();
  const networkStatus = useNetwork();

  const { data: responseData } = useQuery({
    queryKey: ["transactions", user?.email],
    queryFn: fetchTransactions,
    enabled: !!user?.email && networkStatus.online && isWindowFocused,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!responseData) return;

    if (getCookies().get("has-expenses") === "1" && expenses.length === 0) {
      return;
    }

    const existingIds = expenses.map((expense) => expense.id);
    const updates = {
      new: [] as ExpenseWithRequiredCategory[],
      updated: [] as ExpenseWithRequiredCategory[],
    };

    for (const transaction of responseData.transactions) {
      if (existingIds.includes(transaction.trxId)) {
        const index = expenses.findIndex((item) => item.id === transaction.trxId);
        if (index !== -1) {
          updates.updated.push({
            ...expenses[index],
            amount: transaction.amount,
            category: transaction.category,
            date: transaction.date,
            notes: transaction.notes,
            sync: true,
          });
        }
      } else {
        updates.new.push({
          id: transaction.trxId,
          amount: transaction.amount,
          category: transaction.category,
          date: transaction.date,
          notes: transaction.notes,
          sync: true,
        });
      }
    }

    updates.new = updates.new.filter((item) => !existingIds.includes(item.id));

    setExpenses((draft) => {
      draft.expenses.push(...updates.new);

      updates.updated.forEach((item) => {
        const index = draft.expenses.findIndex((expense) => expense.id === item.id);
        if (index !== -1) {
          draft.expenses[index] = item;
        }
      });

      responseData.deletedIds.forEach((id) => {
        const index = draft.expenses.findIndex((item) => item.id === id);
        if (index !== -1) {
          draft.expenses.splice(index, 1);
        }
      });
    });
  }, [networkStatus, user?.email, isWindowFocused]);
};
